'use client';

import React, { useMemo, useCallback } from 'react';
import { Breadcrumb, ConfigProvider, Layout, theme } from 'antd';
import { usePathname, useRouter } from 'next/navigation';
import './styles/Content.css';
import { usePageHeaderActionsStore } from '@/store/zustand/page-header-actions-zustand';
import { dsTheme } from '@/styles/dsTheme';

const { Content } = Layout;

// Nombre de un icono de Material Symbols Rounded (02_lineamientos_visuales.md #2), mismo lenguaje
// visual que ya usan el Sidebar/Navbar/Dashboard (AB#10842).
const Icon = (name: string) => (
  <span className="material-symbols-rounded" style={{ fontSize: 15, verticalAlign: -3 }}>{name}</span>
);

type Props = {
  children: React.ReactNode;
  params?: Record<string, any>;
};

// Función para formatear partes de la ruta a texto amigable
const formatBreadcrumb = (part: string) =>
  part.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();

// Componente DashboardContent
const DashboardContent: React.FC<Props> = ({ children }) => {
  const {
    token: { colorBgContainer, colorPrimary },
  } = theme.useToken();

  const router = useRouter();
  const pathname = usePathname();
  const headerAction = usePageHeaderActionsStore((state) => state.headerAction);

  // Obtener las partes de la ruta como un array de strings
  const pathParts = useMemo(
    () => pathname?.split('/').filter(Boolean) || [],
    [pathname]
  );

  // Memoizar los ítems del breadcrumb
  const breadcrumbItems = useMemo(() => {
    return [
      {
        key: 'home',
        title: <a onClick={() => router.push('/')}>{Icon('home')}</a>,
      },
      ...pathParts.map((part, index) => {
        // El ultimo segmento es la pagina actual, no un link: se distingue visualmente (texto
        // plano, sin azul ni cursor de link) en vez de verse clickeable sin serlo.
        const isCurrent = index === pathParts.length - 1;
        return {
          key: index,
          title: (
            <a
              style={{
                color: isCurrent ? '#232323' : colorPrimary,
                fontWeight: isCurrent ? 600 : 500,
                cursor: isCurrent ? 'default' : 'pointer',
              }}
              onClick={() => {
                if (!isCurrent) {
                  router.push('/' + pathParts.slice(0, index + 1).join('/'));
                }
              }}
            >
              {formatBreadcrumb(part)}
            </a>
          ),
        };
      }),
    ];
  }, [pathParts, router]);

  // Estilos extraídos para reuso
  const contentStyle = {
    margin: '24px 16px 0',
    overflow: 'initial',
    display: 'flex',
    flexDirection: 'column' as const,
  };

  // Design System Soluciones Andinas (02_lineamientos_visuales.md #3): borde + radio 8/12 en vez
  // de radio 5 sin borde, mismo idioma que .customCard en Solicitudes Juridicas (AB#10842).
  const breadcrumbContainerStyle = {
    padding: '14px 16px',
    marginBottom: 15,
    background: colorBgContainer,
    border: '1px solid #E5E5E5',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  };

  const childrenContainerStyle = {
    padding: 10,
    background: colorBgContainer,
    border: '1px solid #E5E5E5',
    borderRadius: 12,
    minHeight: '90%',
    display: 'flex',
    flexDirection: 'column' as const,
  };

  return (
    // Infra compartida por toda la app (envuelve el contenido de cada pagina), mismo criterio que
    // Sidebar/Navbar (AB#10842).
    <ConfigProvider theme={dsTheme}>
      <Content style={contentStyle}>
        <div style={breadcrumbContainerStyle}>
          <Breadcrumb items={breadcrumbItems} />
          {headerAction}
        </div>
        <div style={childrenContainerStyle}>{children}</div>
      </Content>
    </ConfigProvider>
  );
};

export default DashboardContent;
